// DocumentsTab — writable documents surface for record pages.
//
// CONSUMER: internal-ui (advisors). Mounted on Household, Contact,
// Transaction, Opportunity record pages. The same component talks to
// the same backend regardless of which entity hosts it — backend
// figures out the storage path from `entity_type` + `entity_id`.
//
// v1.x phase 11.15. Pairs with the SharePoint backend:
//   * UPLOAD  → POST /api/files/upload (multipart/form-data; single-
//     shot through the Worker; bytes go to SharePoint via Graph)
//   * LIST    → GET  /api/files/documents (existing endpoint;
//     returns SharePoint columns alongside r2_key for legacy rows)
//   * DOWNLOAD → GET /api/files/download/:id (branches on backend;
//     SharePoint-backed rows get a 302 to a Graph downloadUrl that
//     the browser fetches directly from SharePoint's CDN)
//   * OPEN IN SHAREPOINT → GET /api/files/open/:id (advisor-only,
//     never client-facing). Goes through the OS, which grants access
//     to that one file and then redirects. It deliberately does NOT
//     link at the row's raw sharepoint_web_url: employees aren't
//     members of the SharePoint site (the OS holds the credential on
//     their behalf), so the raw address answered "You need access"
//     for everyone but the two site owners — Nate, 2026-08-05.
//   * ARCHIVE → POST /api/files/documents/:id/archive (existing)
//
// 150 MB upload cap (2026-08-11). The API client sends anything over
// 4 MB through the streamed upload-session path; the number here is
// LARGE_UPLOAD_MAX_BYTES in shared/microsoft-graph/graph-client-files.ts.
//
// MULTI-DOCUMENT ENVELOPES (2026-09-01) — Nate Swartz, more than once:
// "I need to add multiple documents together for an esign package...
// he will rarely if ever send one document at a time." Each row already
// eligible for signature gets a checkbox; checking more than one turns
// the per-row "Send for signature" buttons into one bar — "Prepare & send
// N documents" — that opens Prepare & Send with the whole selection as
// ONE envelope (the `documents` prop PrepareAndSend already accepts for
// a package, built for the Realty offer package and the multi-document
// envelope schema, 2026-08-31/09-01). Selecting exactly one behaves
// identically to clicking that row's own button — `documents` is only
// passed to PrepareAndSend when there is more than one, so a one-document
// send still looks and behaves exactly as it always has.
//
// THE RECORD LAYOUT (2026-09-29, Documents tab PR 1 of
// docs/design/documents-tab-workflow.md; drawing
// docs/design/documents-tab-mockup-v3.html screen 1). Opportunity and
// Processing records pass `layout="record"` and get ONE card instead of
// the five the Processing Documents tab used to stack: a header with
// three doors (Upload ▾, Request upload, "+"), the checklist bar the page
// hands in, only the e-sign packages still in flight (Nate Swartz: a
// completed package "is just a signed document at the end of the day"),
// and the two shelves drawn so nobody can mistake one for the other
// (Grant: "I do not want a user to get confused and add a document to
// client when it should be internal"). Households, people, service
// records and the floating dock keep the default layout untouched.

import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Eye,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  Sparkles,
} from "lucide-react";

import {
  ApiError,
  apiV11,
  resolveMediaUrl,
  type DocumentAnalysis,
  type DocumentRec,
  type EsignEnvelopeRec,
  type ServiceRecordPerson,
} from "../api";
import { Button, ErrorBanner } from "../ui";
import { StatusPill } from "./StatusPill";
import { ClientUploadLinkModal } from "./ClientUploadLinkModal";
import { RequestUploadModal } from "./documents/RequestUploadModal";
import { RecordRequestsList } from "./documents/RecordRequestsList";
import { PackageDraftsLine } from "./esign/PackageDraftsLine";
import { PackageWizard } from "./esign/PackageWizard";
import type { PrepareDocument } from "./esign/PrepareAndSend";
import { DocumentPreviewModal } from "./DocumentPreviewModal";
import { OneDriveBrowserModal } from "./OneDriveBrowserModal";
import { RenameDocumentModal } from "./RenameDocumentModal";
import { PrepareAndSend, type CorrectionTarget } from "./esign/PrepareAndSend";
import { readDraftState } from "./esign/package-draft";
import { signedSignerKeys } from "./esign/correction-helpers";
import { SentForSignaturePanel } from "./esign/SentForSignaturePanel";
import { DocumentMoveDialog } from "./DocumentMoveDialog";
import { isEsignSendableKind } from "../../../shared/esign/sendable-kinds";
import { groupPackagePieces } from "../../../shared/documents/source-packages";
import { useToast } from "./Toast";
import { downloadBlob } from "../utils/download-blob";
import { CertificateMergeError, mergeSignedDocumentWithCertificate } from "../lib/esign-download";

export type DocumentsTabEntityType =
  | "household"
  | "contact"
  | "opportunity"
  | "transaction"
  | "service_record"
  | "compliance_checklist_instance";

export interface DocumentsTabProps {
  entityType: DocumentsTabEntityType;
  entityId: string;
  // Display label for the empty state ("No documents on this household yet").
  // Optional — falls back to a generic phrase.
  entityLabel?: string;
  // Joey program PR7 — when true, each SharePoint document gets an
  // "Analyze with Stew" action (income extraction + green/red flags).
  // Passed only from mortgage contexts; server enforces mortgage scope.
  enableStewAnalysis?: boolean;
  // E-signature send half — when provided on any sendable record kind
  // (Processing, Opportunity, service record — isEsignSendableKind is
  // the list), each row gets a "Send for signature" action, and the
  // signer picker is restricted to this
  // exact list — the people already linked to the record (the same
  // list its own People tab shows), never an arbitrary typed email.
  signerCandidates?: ServiceRecordPerson[];
  // Which family this record belongs to. When set, each document gets a
  // "File under…" action offering THIS family's records — scoping it to
  // one household is what makes it impossible to file the Smiths'
  // disclosure onto the Joneses' loan. Absent on records with no
  // household (a licence, a compliance checklist), where the action
  // simply doesn't appear.
  householdId?: string | null;
  // Fires once a document is successfully sent for signature — lets a
  // sibling status panel (SentForSignaturePanel) know to refetch rather
  // than waiting for the next page load to show the new envelope.
  onSignatureSent?: () => void;
  /** "record" is the one-card Documents tab for Opportunity and
   *  Processing records (see the header). Anything else keeps the
   *  original layout. */
  layout?: "default" | "record";
  /** Record layout only: the compliance checklist bar, rendered under
   *  the header. The page owns it because the checklist data and its
   *  drawer live there. */
  checklistBar?: ReactNode;
  /** Record layout only: what "+ → Checklist for this file" does. Null
   *  or absent shows the item as unavailable with the reason below. */
  onOpenChecklist?: (() => void) | null;
  /** The checklist drawer's "Draft now" (Nate, 2026-10-26/27): open the
   *  package wizard on this template or single form, or on neither (it then
   *  offers the list). The nonce lets the same one be asked for twice in a row. */
  prepareRequest?: {
    templatePackageId: string | null;
    esignTemplateId?: string | null;
    nonce: number;
  } | null;
  /** Called once the request has opened the wizard, so the page clears it
   *  and coming back to this tab doesn't open the wizard again. */
  onPrepareRequestHandled?: () => void;
  checklistUnavailableReason?: string;
  /** Record layout only: anything the page still needs under the
   *  shelves (the Realty contract packet, the originating opportunity's
   *  files) until its own PR folds it in. */
  afterShelves?: ReactNode;
  /** Bump to reload the shelves when something outside the card filed a
   *  document here (the checklist drawer's Upload, Documents tab PR 4). */
  refreshKey?: number;
  /** Called after the card's own list reloads following a change here
   *  (an upload, a rename, a move), so the checklist bar can recount. */
  onDocumentsChanged?: () => void;
}

// Mirrors LARGE_UPLOAD_MAX_BYTES on the server. Checked here only so
// an over-size pick fails instantly instead of after a long upload.
const MAX_UPLOAD_BYTES = 150 * 1024 * 1024;

function humanFileSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function backendOf(doc: DocumentRec): "sharepoint" | "r2" {
  return doc.sharepoint_item_id ? "sharepoint" : "r2";
}

// Fail-safe, not a strict equality check against "internal": anything
// that isn't exactly "client" sorts internal, so a row whose visibility
// is missing or unrecognized (a stale cached fetch, an older test
// fixture) still shows up SOMEWHERE rather than silently vanishing from
// both shelves. The safe default matches the column's own DB default.
function shelfOf(doc: DocumentRec): "client" | "internal" {
  return doc.visibility === "client" ? "client" : "internal";
}

function formatUploadedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  const opts: Intl.DateTimeFormatOptions = sameYear
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" };
  return d.toLocaleDateString(undefined, opts);
}

export function DocumentsTab({
  entityType,
  entityId,
  entityLabel,
  enableStewAnalysis = false,
  signerCandidates,
  householdId = null,
  onSignatureSent,
  layout = "default",
  checklistBar,
  onOpenChecklist = null,
  prepareRequest = null,
  onPrepareRequestHandled,
  checklistUnavailableReason = "No checklist for this kind of file yet.",
  afterShelves,
  refreshKey = 0,
  onDocumentsChanged,
}: DocumentsTabProps) {
  const isRecord = layout === "record";
  // Teammate requests exist for Opportunity and Processing files only
  // (they are what carries a checklist).
  const canRequest = isRecord && (entityType === "opportunity" || entityType === "transaction");
  const toast = useToast();
  const [docs, setDocs] = useState<DocumentRec[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  // The document being re-filed, and the undo handed back by the move.
  const [moveTarget, setMoveTarget] = useState<DocumentRec | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // E-signature send half — the document currently open in the "Send
  // for signature" modal, or null when it's closed.
  const [sendTarget, setSendTarget] = useState<DocumentRec | null>(null);
  // Multi-document envelope (2026-09-01) — ids of documents checked for
  // a combined send, and (once "Prepare & send" is clicked) the actual
  // documents that opens Prepare & Send with. Two separate pieces of
  // state because clearing the checkboxes on close/send must not also
  // yank the documents out from under an open Prepare & Send screen.
  const [selectedForEnvelope, setSelectedForEnvelope] = useState<Set<string>>(() => new Set());
  const [envelopeDocs, setEnvelopeDocs] = useState<DocumentRec[] | null>(null);
  // The package wizard (Documents tab PR 8a): step 1 here, then Prepare &
  // Send on the forms it put on the record.
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardPackageId, setWizardPackageId] = useState<string | null>(null);
  // HELP-OK: the user-visible change (Draft now, tick-several collection, single forms) is documented in checklist-drawer and admin-edit-checklist, both re-verified; the other articles citing this file, RecordChecklist and the two record pages describe flows this PR did not change.
  const [wizardFormId, setWizardFormId] = useState<string | null>(null);
  useEffect(() => {
    if (!prepareRequest) return;
    setWizardPackageId(prepareRequest.templatePackageId);
    setWizardFormId(prepareRequest.esignTemplateId ?? null);
    setWizardOpen(true);
    onPrepareRequestHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only a new request should reopen it
  }, [prepareRequest]);
  const [wizardDocs, setWizardDocs] = useState<PrepareDocument[] | null>(null);
  // A draft opened from the draft line (PR 8c): its documents go in
  // wizardDocs like any package; this carries the rest of it.
  const [openDraft, setOpenDraft] = useState<{ id: string; versionNumber: number; state: unknown; realtyOfferId?: string | null } | null>(
    null,
  );
  const [draftsKey, setDraftsKey] = useState(0);
  // "Fix what hasn't been signed yet" (PR 10b): the package the send
  // screen is correcting, when it is.
  const [correction, setCorrection] = useState<CorrectionTarget | null>(null);

  // The one test every checkbox and every "send for signature" button
  // shares — kept in one place so they can never disagree about which
  // rows are eligible.
  const canSendForSignature = useCallback(
    (doc: DocumentRec) =>
      Boolean(
        signerCandidates &&
          signerCandidates.length > 0 &&
          isEsignSendableKind(entityType) &&
          // Never offer to (re-)send an already-signed document. See the
          // long-form reasoning on the row render below.
          doc.signed_status !== "fully_signed",
      ),
    [signerCandidates, entityType],
  );

  const toggleEnvelopeSelection = useCallback((docId: string) => {
    setSelectedForEnvelope((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  }, []);

  // Default to ONE document, allow both (Grant, 2026-09-20). A package
  // envelope's combined signed copy and the per-form pieces cut from it
  // (the auto-split, 2026-09-09 — Joey's mortgage close-out depends on
  // those pieces existing as their own rows) used to render as N+1 flat
  // rows for one signature. groupPackagePieces (shared/documents/
  // source-packages.ts) does the actual grouping, pure and unit-tested;
  // the combined copy is what shows by default, and its pieces sit one
  // click away — never removed from the record and never hidden from
  // data-layer reads (checklists, exports — everything that isn't this
  // rendering sees every document exactly as before).
  const packageGrouping = useMemo(() => groupPackagePieces(docs), [docs]);

  const [expandedPackages, setExpandedPackages] = useState<Set<string>>(() => new Set());
  const togglePackageExpanded = useCallback((docId: string) => {
    setExpandedPackages((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  }, []);

  // Pre-upload review state — when user picks a file, we stash it
  // here and pop the rename modal. The actual upload only fires once
  // the user confirms (or edits) the filename.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  // Post-upload rename target (the document being renamed).
  const [renameTarget, setRenameTarget] = useState<DocumentRec | null>(null);
  // Inline-preview target (the document being previewed in an iframe).
  const [previewTarget, setPreviewTarget] = useState<DocumentRec | null>(null);
  // v1.x phase 11.16 (Tier B item 13): copy-from-OneDrive modal.
  // Toggled via "Copy from OneDrive" button.
  const [oneDriveOpen, setOneDriveOpen] = useState(false);
  // v1.x phase 11.16 (Tier B item 11): client-upload magic-link modal.
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  // Documents tab PR 6a: "Request upload" on an Opportunity or Processing
  // file asks a teammate, in the OS. The key reloads the open-requests
  // list after a send.
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestsKey, setRequestsKey] = useState(0);
  // Record layout: the header menus, the package list's envelopes (so a
  // signed file can link to its signing history), the row waiting on
  // "Share it", and a key that reloads the packages after a send.
  const [uploadMenuOpen, setUploadMenuOpen] = useState(false);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [envelopes, setEnvelopes] = useState<EsignEnvelopeRec[]>([]);
  const [shareConfirmId, setShareConfirmId] = useState<string | null>(null);
  const [packagesKey, setPackagesKey] = useState(0);
  const blankPackageInputRef = useRef<HTMLInputElement | null>(null);
  // Which package each signed file came from (PR 9a): the whole signed
  // copy, its certificate, and each document cut from it, so every one
  // of them names its package and opens its "Signers & history". Read
  // from the envelope's own links (sealed_document_id,
  // audit_certificate_document_id) and each piece's split_from_document_id,
  // rather than a second copy of the link on the file.
  const envelopeBySealedDoc = useMemo(() => {
    const map = new Map<string, EsignEnvelopeRec>();
    for (const e of envelopes) {
      if (e.sealedDocumentId) map.set(e.sealedDocumentId, e);
      if (e.auditCertificateDocumentId) map.set(e.auditCertificateDocumentId, e);
    }
    for (const d of docs) {
      const whole = d.split_from_document_id ? map.get(d.split_from_document_id) : undefined;
      if (whole && !map.has(d.id)) map.set(d.id, whole);
    }
    return map;
  }, [envelopes, docs]);
  const canStartPackage = Boolean(
    signerCandidates && signerCandidates.length > 0 && isEsignSendableKind(entityType),
  );

  const refreshList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const resp = await apiV11.listDocumentsForEntity(entityType, entityId);
      setDocs(resp.items);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Couldn't load documents.";
      setListError(message);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    void refreshList();
    // A stale selection pointing at another record's documents is worse
    // than an empty one.
    setSelectedForEnvelope(new Set());
    // refreshKey is a deliberate dependency: bumping it is the request to
    // reload.
  }, [refreshList, refreshKey]);

  // Tell the page when this card changed the record's documents (not on
  // the first load, and not for a reload the page itself asked for), so a
  // checklist item whose type was just filed can recount.
  const docsVersion = useRef(0);
  const lastRefreshKey = useRef(refreshKey);
  useEffect(() => {
    docsVersion.current += 1;
    if (docsVersion.current === 1) return;
    if (lastRefreshKey.current !== refreshKey) {
      lastRefreshKey.current = refreshKey;
      return;
    }
    onDocumentsChanged?.();
    // Only a change to the list itself should fire this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs]);

  // Step 1 of upload flow — file picked. Validate size, then stash
  // for the rename-modal step. Actual upload runs in commitUpload.
  const handleFile = useCallback((file: File) => {
    setUploadError(null);
    if (file.size === 0) {
      setUploadError("That file is empty.");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError(`File too large (${humanFileSize(file.size)}). 150 MB max.`);
      return;
    }
    setPendingFile(file);
  }, []);

  // Step 2 of upload flow — user confirmed (or edited) the filename
  // in the rename modal. Wrap the original File with the new name
  // (browser's File constructor does this cheaply — same bytes,
  // different metadata) and POST to /api/files/upload.
  const commitUpload = useCallback(
    async (chosenFilename: string) => {
      if (!pendingFile) return;
      setUploading(true);
      try {
        const renamed = new File([pendingFile], chosenFilename, {
          type: pendingFile.type,
          lastModified: pendingFile.lastModified,
        });
        const resp = await apiV11.uploadDocumentSharePoint(renamed, {
          entity_type: entityType,
          entity_id: entityId,
        });
        // Optimistic prepend — the response carries the persisted
        // row (with sharepoint columns populated) so we don't need
        // to re-list.
        setDocs((prev) => [resp.document, ...prev]);
        toast.success(`Uploaded ${resp.document.filename}.`);
        setPendingFile(null);
      } catch (err) {
        const message = err instanceof ApiError ? err.message : "Upload failed.";
        setUploadError(message);
        toast.error(message);
        // Re-throw so the modal stays open and surfaces the error.
        throw err instanceof Error ? err : new Error(message);
      } finally {
        setUploading(false);
      }
    },
    [pendingFile, entityType, entityId, toast],
  );

  // Several files dropped or picked at once upload in turn under their
  // own names. One file still goes through "Review filename"; a naming
  // step per file for a stack of ten is the friction this saves.
  // Every layout, not only the record card (2026-10-15, Andrea
  // Middleton): the side panel used to keep files[0] and silently drop
  // the rest of a multi-file drop.
  const uploadMany = useCallback(
    async (files: File[]) => {
      setUploadError(null);
      const ok: DocumentRec[] = [];
      setUploading(true);
      try {
        for (const file of files) {
          if (file.size === 0 || file.size > MAX_UPLOAD_BYTES) {
            toast.error(
              file.size === 0
                ? `${file.name} is empty.`
                : `${file.name} is too large (${humanFileSize(file.size)}). 150 MB max.`,
            );
            continue;
          }
          try {
            const resp = await apiV11.uploadDocumentSharePoint(file, {
              entity_type: entityType,
              entity_id: entityId,
            });
            ok.push(resp.document);
          } catch (err) {
            toast.error(
              `${file.name}: ${err instanceof ApiError ? err.message : "upload failed."}`,
            );
          }
        }
      } finally {
        setUploading(false);
      }
      if (ok.length > 0) {
        setDocs((prev) => [...ok, ...prev]);
        toast.success(`Uploaded ${ok.length} file${ok.length === 1 ? "" : "s"}.`);
      }
    },
    [entityType, entityId, toast],
  );

  const handleFiles = useCallback(
    (list: FileList | null | undefined) => {
      const files = Array.from(list ?? []);
      if (files.length === 0) return;
      if (files.length === 1) handleFile(files[0]!);
      else void uploadMany(files);
    },
    [handleFile, uploadMany],
  );

  // "+ → Blank e-sign package": pick a PDF, it lands on the Internal
  // shelf like any upload, and Prepare & Send opens on it at once. The
  // four-step package wizard replaces this door in a later PR.
  const startBlankPackage = useCallback(
    async (file: File) => {
      if (file.size === 0 || file.size > MAX_UPLOAD_BYTES) {
        toast.error(file.size === 0 ? "That file is empty." : "That file is over 150 MB.");
        return;
      }
      setUploading(true);
      try {
        const resp = await apiV11.uploadDocumentSharePoint(file, {
          entity_type: entityType,
          entity_id: entityId,
        });
        setDocs((prev) => [resp.document, ...prev]);
        setSendTarget(resp.document);
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : "Upload failed.");
      } finally {
        setUploading(false);
      }
    },
    [entityType, entityId, toast],
  );

  // Post-upload rename. Hits PATCH /api/files/documents/:id which
  // renames in SharePoint via Graph + mirrors to D1.
  const commitRename = useCallback(
    async (newFilename: string) => {
      if (!renameTarget) return;
      try {
        const resp = await apiV11.renameDocument(renameTarget.id, newFilename);
        setDocs((prev) => prev.map((d) => (d.id === resp.document.id ? resp.document : d)));
        toast.success("Renamed.");
        setRenameTarget(null);
      } catch (err) {
        const message = err instanceof ApiError ? err.message : "Rename failed.";
        toast.error(message);
        throw err instanceof Error ? err : new Error(message);
      }
    },
    [renameTarget, toast],
  );

  const handleArchive = useCallback(
    async (doc: DocumentRec) => {
      const ok = (globalThis as unknown as { confirm?: (m: string) => boolean }).confirm?.(
        `Archive "${doc.filename}"? It hides from this list. SharePoint keeps the file in the Recycle Bin for 93 days.`,
      );
      if (!ok) return;
      try {
        await apiV11.archiveDocument(doc.id);
        setDocs((prev) => prev.filter((d) => d.id !== doc.id));
        toast.success("Document archived.");
      } catch (err) {
        const message = err instanceof ApiError ? err.message : "Couldn't archive.";
        toast.error(message);
      }
    },
    [toast],
  );

  // "Share with client" / "Move to internal" (Nate's Aug 24 spec §9,
  // PR5 of 5) — flips a document's shelf. Fully reversible (one more
  // click undoes it), so no confirm dialog, same posture as rename.
  // The record layout adds one inline question before SHARING (Grant,
  // 2026-09-29), never before narrowing: widening who can see a file is
  // the mistake worth a second look.
  const handleSetVisibility = useCallback(
    async (doc: DocumentRec, visibility: "client" | "internal") => {
      try {
        const resp = await apiV11.setDocumentVisibility(doc.id, visibility);
        setDocs((prev) => prev.map((d) => (d.id === resp.document.id ? resp.document : d)));
        toast.success(
          visibility === "client"
            ? `Shared "${doc.filename}" with the client.`
            : `Moved "${doc.filename}" to internal.`,
        );
      } catch (err) {
        const message = err instanceof ApiError ? err.message : "Couldn't change visibility.";
        toast.error(message);
      }
    },
    [toast],
  );

  // The full per-row prop wiring, factored out so a package's pieces
  // render through the exact same path as any standalone document
  // (preview, archive, move, send — nothing about a piece behaves
  // differently just because it's shown one click deeper) rather than
  // a second, easier-to-drift copy of this prop list.
  const renderDocumentRow = useCallback(
    (doc: DocumentRec) => (
      <DocumentRow
        key={doc.id}
        doc={doc}
        onArchive={() => void handleArchive(doc)}
        onRename={() => setRenameTarget(doc)}
        onToggleVisibility={() =>
          void handleSetVisibility(doc, doc.visibility === "client" ? "internal" : "client")
        }
        onPreview={doc.sharepoint_item_id ? () => setPreviewTarget(doc) : null}
        enableStewAnalysis={enableStewAnalysis}
        onMove={householdId ? () => setMoveTarget(doc) : null}
        onSendForSignature={canSendForSignature(doc) ? () => setSendTarget(doc) : null}
        selectableForEnvelope={canSendForSignature(doc)}
        selectedForEnvelope={selectedForEnvelope.has(doc.id)}
        onToggleEnvelopeSelection={() => toggleEnvelopeSelection(doc.id)}
        compact={isRecord}
        envelope={envelopeBySealedDoc.get(doc.id) ?? null}
        shareConfirmOpen={shareConfirmId === doc.id}
        onRequestShare={() => setShareConfirmId(doc.id)}
        onCancelShare={() => setShareConfirmId(null)}
        onConfirmShare={() => {
          setShareConfirmId(null);
          void handleSetVisibility(doc, "client");
        }}
      />
    ),
    [
      isRecord,
      envelopeBySealedDoc,
      shareConfirmId,
      handleArchive,
      handleSetVisibility,
      enableStewAnalysis,
      householdId,
      canSendForSignature,
      selectedForEnvelope,
      toggleEnvelopeSelection,
    ],
  );

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      handleFiles(event.dataTransfer.files);
    },
    [handleFiles],
  );

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  }, []);

  const onDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  }, []);

  const trailing = (
    <>
      {/* Multi-document envelope bar (2026-09-01) — Nate Swartz, more
          than once: sending several documents already on this record as
          one signing request instead of one-at-a-time. Appears the
          moment anything is checked; a single checked document behaves
          exactly like that row's own "Send for signature" button. */}
      {selectedForEnvelope.size > 0 ? (
        <div className="documents-tab-envelope-bar">
          <span className="documents-tab-envelope-bar-count">
            {selectedForEnvelope.size} document{selectedForEnvelope.size === 1 ? "" : "s"} selected
          </span>
          <Button
            onClick={() => {
              const chosen = docs
                .filter((d) => selectedForEnvelope.has(d.id))
                // Preserve on-screen order (client shelf, then internal)
                // rather than selection order, so the envelope reads the
                // way the list reads.
                .sort((a, b) => docs.indexOf(a) - docs.indexOf(b));
              setEnvelopeDocs(chosen);
            }}
          >
            {selectedForEnvelope.size === 1
              ? "Prepare & send"
              : `Prepare & send ${selectedForEnvelope.size} documents`}
          </Button>
          <Button variant="ghost" onClick={() => setSelectedForEnvelope(new Set())}>
            Clear
          </Button>
        </div>
      ) : null}
      {/* E-signature Prepare & Send — the full-screen prep experience
          (field placement + send; replaced the old checkbox modal,
          2026-08-22). Reachable from Processing records, Opportunities
          and service records (2026-08-23) — the three kinds the send
          route accepts. signerCandidates is the record's own linked-
          people list, so the picker can never name someone who isn't
          already on this record. Households and contacts mount this tab
          too and send like anything else as of 2026-08-24 — the earlier
          rule ("a signature belongs to a piece of work, not to a filing
          cabinet") only pushed those documents out of the OS. Also the
          landing spot for the multi-document envelope bar above: when
          envelopeDocs is set, its first entry fills the same
          documentId/documentFilename/documentType props every other
          caller passes (so a one-document selection behaves exactly
          like clicking that row's own button), and `documents` — passed
          only once there's more than one — turns on the document rail. */}
      {signerCandidates && isEsignSendableKind(entityType) ? (
        <PrepareAndSend
          open={sendTarget !== null || envelopeDocs !== null || wizardDocs !== null}
          onClose={() => {
            setSendTarget(null);
            setEnvelopeDocs(null);
            setWizardDocs(null);
            setOpenDraft(null);
            setCorrection(null);
          }}
          correction={correction}
          onCorrected={() => {
            setWizardDocs(null);
            setOpenDraft(null);
            setCorrection(null);
            setPackagesKey((k) => k + 1);
          }}
          recordKind={entityType}
          recordId={entityId}
          eyebrow={
            openDraft
              ? "E-sign package draft · pick up where you left off"
              : wizardDocs
                ? "New e-sign package · signers, fields and send"
                : null
          }
          draft={openDraft}
          // The offer a reopened draft or a correction was drafted from,
          // so its preview fills that offer's price and agents the same
          // way the send will (Nate, 2026-10-15).
          realtyOfferId={openDraft?.realtyOfferId ?? null}
          onDraftSaved={() => setDraftsKey((k) => k + 1)}
          // A package from the wizard or a draft opens on "Who signs, and
          // in what order" (step 2, PR 8d); a single document's own
          // "Prepare & send" opens straight on its fields, as before.
          startOnSigners={wizardDocs !== null && correction === null}
          documentId={wizardDocs?.[0]?.documentId ?? envelopeDocs?.[0]?.id ?? sendTarget?.id ?? ""}
          documentFilename={
            wizardDocs?.[0]?.filename ?? envelopeDocs?.[0]?.filename ?? sendTarget?.filename ?? ""
          }
          documentType={
            wizardDocs?.[0]?.documentType ??
            envelopeDocs?.[0]?.document_type ??
            sendTarget?.document_type ??
            null
          }
          // exactOptionalPropertyTypes means `documents` must be omitted
          // entirely for one document, not passed as `undefined` — that's
          // what tells Prepare & Send to render with no document rail at
          // all rather than a rail with one greyed-out entry.
          {...(wizardDocs && wizardDocs.length > 1
            ? { documents: wizardDocs }
            : envelopeDocs && envelopeDocs.length > 1
            ? {
                documents: envelopeDocs.map((d) => ({
                  documentId: d.id,
                  filename: d.filename,
                  documentType: d.document_type ?? null,
                })),
              }
            : {})}
          candidates={signerCandidates}
          onSent={() => {
            setSendTarget(null);
            setEnvelopeDocs(null);
            setWizardDocs(null);
            setOpenDraft(null);
            setDraftsKey((k) => k + 1);
            setSelectedForEnvelope(new Set());
            setPackagesKey((k) => k + 1);
            onSignatureSent?.();
          }}
        />
      ) : null}
      {/* Re-filing. The undo is offered as a toast rather than a
          persistent banner: a move is easy to reverse and the moment of
          doubt is right after doing it, not tomorrow. */}
      <DocumentMoveDialog
        open={moveTarget !== null}
        onClose={() => setMoveTarget(null)}
        documentId={moveTarget?.id ?? ""}
        documentName={moveTarget?.filename ?? ""}
        householdId={householdId}
        currentEntityType={entityType}
        currentEntityId={entityId}
        onMoved={(undo) => {
          void refreshList();
          toast.success(`Moved "${moveTarget?.filename ?? "the document"}".`, {
            action: {
              label: "Undo",
              onClick: () => {
                void (async () => {
                  try {
                    await undo();
                    void refreshList();
                    toast.success("Put it back.");
                  } catch {
                    toast.error("Couldn't undo that move.");
                  }
                })();
              },
            },
          });
        }}
      />

      {/* Pre-upload rename modal — pops when user picks a file. */}
      <RenameDocumentModal
        open={pendingFile !== null}
        initialFilename={pendingFile?.name ?? ""}
        title="Review filename"
        submitLabel="Upload"
        onSubmit={commitUpload}
        onCancel={() => {
          if (!uploading) setPendingFile(null);
        }}
      />
      {/* Post-upload rename modal — pops from the pencil icon. */}
      <RenameDocumentModal
        open={renameTarget !== null}
        initialFilename={renameTarget?.filename ?? ""}
        title="Rename file"
        submitLabel="Save"
        onSubmit={commitRename}
        onCancel={() => setRenameTarget(null)}
      />
      {/* Inline preview — pops from the eye icon on each row. */}
      <DocumentPreviewModal
        open={previewTarget !== null}
        documentId={previewTarget?.id ?? null}
        filename={previewTarget?.filename ?? ""}
        sharepointWebUrl={previewTarget?.sharepoint_web_url ?? null}
        onClose={() => setPreviewTarget(null)}
        onArchived={() => void refreshList()}
      />
      {/* v1.x phase 11.16 (Tier B item 13): copy from OneDrive. */}
      <OneDriveBrowserModal
        open={oneDriveOpen}
        onClose={() => setOneDriveOpen(false)}
        entityType={entityType}
        entityId={entityId}
        onCopied={(doc) => {
          setDocs((prev) => [doc, ...prev]);
          toast.success(`Copied ${doc.filename} from OneDrive.`);
        }}
      />
      {/* v1.x phase 11.16 (Tier B item 11): client-upload links. */}
      {entityType === "household" || entityType === "contact" ? (
        <ClientUploadLinkModal
          open={linkModalOpen}
          onClose={() => setLinkModalOpen(false)}
          entityType={entityType}
          entityId={entityId}
          defaultRecipientLabel={entityLabel ?? null}
        />
      ) : isRecord && householdId ? (
        // "Send the client a link instead" from the request window: until
        // the client half of requests ships (PR 6b), this is today's
        // client upload link, minted for the record's household so the
        // files are the family's.
        <ClientUploadLinkModal
          open={linkModalOpen}
          onClose={() => setLinkModalOpen(false)}
          entityType="household"
          entityId={householdId}
          defaultRecipientLabel={null}
        />
      ) : null}
      {canRequest && canStartPackage ? (
        <PackageWizard
          open={wizardOpen}
          onClose={() => {
            setWizardOpen(false);
            setWizardPackageId(null);
            setWizardFormId(null);
          }}
          initialPackageId={wizardPackageId}
          initialFormId={wizardFormId}
          kind={entityType as "opportunity" | "transaction"}
          recordId={entityId}
          recordLabel={entityLabel ?? null}
          onReady={(documents) => {
            setWizardOpen(false);
            setWizardPackageId(null);
            setWizardFormId(null);
            setWizardDocs(documents);
            void refreshList();
          }}
          // Only the client shelf: an attachment goes to the client, and a
          // file kept internal must never ride along by accident.
          recordDocuments={docs
            .filter(
              (d) =>
                !d.archived &&
                d.visibility === "client" &&
                (d.mime_type === "application/pdf" || /\.pdf$/i.test(d.filename)),
            )
            .map((d) => ({
              id: d.id,
              filename: d.filename,
              document_type: d.document_type,
              created_at: d.uploaded_at,
            }))}
          onDraftSaved={() => {
            setDraftsKey((k) => k + 1);
            void refreshList();
          }}
        />
      ) : null}
      {canRequest ? (
        <RequestUploadModal
          open={requestModalOpen}
          onClose={() => setRequestModalOpen(false)}
          kind={entityType as "opportunity" | "transaction"}
          recordId={entityId}
          onSent={() => setRequestsKey((k) => k + 1)}
          onSendClientLink={householdId ? () => setLinkModalOpen(true) : null}
        />
      ) : null}
    </>
  );

  if (isRecord) {
    const clientDocs = docs.filter(
      (d) => shelfOf(d) === "client" && !packageGrouping.childIds.has(d.id),
    );
    const internalDocs = docs.filter(
      (d) => shelfOf(d) === "internal" && !packageGrouping.childIds.has(d.id),
    );
    const renderList = (list: DocumentRec[]) => (
      <ul className="documents-tab-list">
        {list.map((doc) => {
          const pieces = packageGrouping.childrenByParent.get(doc.id);
          if (!pieces) return renderDocumentRow(doc);
          const expanded = expandedPackages.has(doc.id);
          return (
            <Fragment key={doc.id}>
              {renderDocumentRow(doc)}
              <li className="documents-tab-package-detail">
                <button
                  type="button"
                  className="documents-tab-package-toggle"
                  onClick={() => togglePackageExpanded(doc.id)}
                  aria-expanded={expanded}
                >
                  {expanded
                    ? "▾ Hide separate documents"
                    : `▸ Show ${pieces.length} separate document${pieces.length === 1 ? "" : "s"}`}
                </button>
                {expanded ? (
                  <ul className="documents-tab-list documents-tab-package-children">
                    {pieces.map((piece) => renderDocumentRow(piece))}
                  </ul>
                ) : null}
              </li>
            </Fragment>
          );
        })}
      </ul>
    );
    return (
      <div
        className={`documents-tab doc-card${dragActive ? " is-drag-active" : ""}`}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <input
          ref={blankPackageInputRef}
          type="file"
          accept="application/pdf,.pdf"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void startBlankPackage(file);
            e.target.value = "";
          }}
        />
        <header className="doc-card-head">
          <div>
            <h2 className="doc-card-title">Documents</h2>
            <p className="doc-card-sub">
              Everything saved to SharePoint for audit. You choose what the client can see.
            </p>
          </div>
          <div className="doc-card-actions">
            <DocMenu
              open={uploadMenuOpen}
              onOpenChange={setUploadMenuOpen}
              trigger={
                <>
                  {uploading ? "Uploading…" : "Upload"}
                  <ChevronDown size={13} strokeWidth={2} aria-hidden />
                </>
              }
              triggerClassName="doc-btn"
              label="Upload"
            >
              <button
                type="button"
                role="menuitem"
                className="doc-menu-item"
                disabled={uploading}
                onClick={() => {
                  setUploadMenuOpen(false);
                  inputRef.current?.click();
                }}
              >
                Upload files
                <small>Or drop them anywhere on this card</small>
              </button>
              <button
                type="button"
                role="menuitem"
                className="doc-menu-item"
                onClick={() => {
                  setUploadMenuOpen(false);
                  setOneDriveOpen(true);
                }}
              >
                Copy from OneDrive
                <small>Copies a file from your OneDrive into this record</small>
              </button>
            </DocMenu>
            {canRequest ? (
              <button type="button" className="doc-btn" onClick={() => setRequestModalOpen(true)}>
                Request upload
              </button>
            ) : householdId ? (
              <button type="button" className="doc-btn" onClick={() => setLinkModalOpen(true)}>
                Request upload
              </button>
            ) : null}
            <DocMenu
              open={plusMenuOpen}
              onOpenChange={setPlusMenuOpen}
              trigger={<Plus size={18} strokeWidth={2} aria-hidden />}
              triggerClassName="doc-btn doc-btn--primary doc-btn--plus"
              label="Add a package or a checklist"
            >
              {canRequest ? (
                <button
                  type="button"
                  role="menuitem"
                  className="doc-menu-item"
                  disabled={!canStartPackage || uploading}
                  onClick={() => {
                    setPlusMenuOpen(false);
                    setWizardOpen(true);
                  }}
                >
                  New e-sign package
                  <small>
                    {canStartPackage
                      ? "Templates, library forms, this file, your computer or OneDrive"
                      : "Add a person to this record first — they are who signs"}
                  </small>
                </button>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  className="doc-menu-item"
                  disabled={!canStartPackage || uploading}
                  onClick={() => {
                    setPlusMenuOpen(false);
                    blankPackageInputRef.current?.click();
                  }}
                >
                  Blank e-sign package
                  <small>
                    {canStartPackage
                      ? "Upload a PDF, then place the fields and send"
                      : "Add a person to this record first — they are who signs"}
                  </small>
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className="doc-menu-item"
                disabled={!onOpenChecklist}
                onClick={() => {
                  setPlusMenuOpen(false);
                  onOpenChecklist?.();
                }}
              >
                Checklist for this file
                <small>
                  {onOpenChecklist ? "Open the compliance checklist" : checklistUnavailableReason}
                </small>
              </button>
              {/* The Checklists area (2026-09-29): see or change what a
                  checklist asks for. Read-only for anyone without the
                  company's edit authority. */}
              <Link
                role="menuitem"
                className="doc-menu-item"
                to="/checklists"
                onClick={() => setPlusMenuOpen(false)}
              >
                Manage checklists
                <small>What each checklist asks for, for every company</small>
              </Link>
            </DocMenu>
          </div>
        </header>

        {checklistBar ?? null}

        {canRequest ? (
          <RecordRequestsList
            kind={entityType as "opportunity" | "transaction"}
            recordId={entityId}
            refreshKey={requestsKey + refreshKey + docs.length}
          />
        ) : null}

        {isEsignSendableKind(entityType) ? (
          <SentForSignaturePanel
            key={packagesKey}
            recordKind={entityType}
            recordId={entityId}
            variant="inflight"
            onEnvelopesLoaded={setEnvelopes}
            onResendPackage={
              signerCandidates
                ? (envelope) => {
                    // Only reached when onOpenDraft is not given (a record
                    // where packages can't be started): the signing screen
                    // on the package's document, signers chosen anew.
                    const source = docs.find((d) => d.id === envelope.sourceDocumentId);
                    if (source) setSendTarget(source);
                    else
                      toast.error(
                        "The document this package was sent from isn't on this record any more. Start a new package from the file.",
                      );
                  }
                : null
            }
            onOpenCorrection={
              canRequest && canStartPackage
                ? (envelope, copy, reason) => {
                    const st = readDraftState(copy.state);
                    const signed = signedSignerKeys(
                      envelope.signers.map((sg) => ({
                        contactId: sg.contactId,
                        email: sg.email,
                        name: sg.name,
                        signed: Boolean(sg.signedAt) || sg.status === "signed",
                      })),
                      st?.guests ?? [],
                    );
                    setCorrection({
                      envelopeId: envelope.id,
                      title: envelope.title ?? "this package",
                      reason,
                      signedKeys: signed.keys,
                      signedNames: signed.names,
                    });
                    // Never saved: the id only keys the screen's own state.
                    setOpenDraft({
                      id: `correction:${envelope.id}`,
                      versionNumber: 0,
                      state: copy.state,
                      realtyOfferId: copy.realtyOfferId ?? null,
                    });
                    setWizardDocs(
                      copy.documents.map((doc) => ({
                        documentId: doc.documentId,
                        filename: doc.filename,
                        documentType: doc.documentType,
                        origin: doc.origin,
                        attachment: doc.attachment,
                      })),
                    );
                  }
                : null
            }
            onOpenDraft={
              canRequest && canStartPackage
                ? (d, info) => {
                    // A package copied back into a draft (PR 10a): it opens
                    // exactly as "Continue" opens a draft from the line.
                    setDraftsKey((k) => k + 1);
                    if (info.droppedDocuments.length > 0) {
                      toast.error(
                        `${info.droppedDocuments.join(", ")} ${info.droppedDocuments.length === 1 ? "isn't" : "aren't"} on this record any more, so ${info.droppedDocuments.length === 1 ? "it was" : "they were"} left out.`,
                      );
                    }
                    if (!info.fieldsKept) {
                      toast.success(
                        "This package was sent before the OS kept a package's fields, so the signers, order and email came across and the fields are suggested again. Check them before you send.",
                      );
                    }
                    setOpenDraft({ id: d.id, versionNumber: d.versionNumber, state: d.state, realtyOfferId: d.realtyOfferId ?? null });
                    setWizardDocs(
                      d.documents.map((doc) => ({
                        documentId: doc.documentId,
                        filename: doc.filename,
                        documentType: doc.documentType,
                        origin: doc.origin,
                        attachment: doc.attachment,
                      })),
                    );
                  }
                : null
            }
          />
        ) : null}
        {canRequest && canStartPackage ? (
          <PackageDraftsLine
            kind={entityType as "opportunity" | "transaction"}
            recordId={entityId}
            refreshKey={draftsKey}
            onContinue={(d) => {
              setOpenDraft({ id: d.id, versionNumber: d.versionNumber, state: d.state, realtyOfferId: d.realtyOfferId ?? null });
              setWizardDocs(
                d.documents.map((doc) => ({
                  documentId: doc.documentId,
                  filename: doc.filename,
                  documentType: doc.documentType,
                  origin: doc.origin,
                  attachment: doc.attachment,
                })),
              );
            }}
          />
        ) : null}

        {uploadError ? <ErrorBanner message={uploadError} /> : null}
        {listError ? <ErrorBanner message={listError} /> : null}
        {loading ? (
          <p className="documents-tab-loading">Loading documents…</p>
        ) : (
          <>
            <section className="doc-shelf doc-shelf--client" aria-label="Visible to client">
              <h3 className="doc-card-sec">
                <span className="doc-shelf-title">
                  <Eye size={16} strokeWidth={1.5} aria-hidden />
                  Visible to client · {clientDocs.length}
                </span>
                <small>The client can open these</small>
              </h3>
              {clientDocs.length === 0 ? (
                <p className="documents-tab-shelf-empty doc-shelf-empty">
                  Nothing shared with the client yet.
                </p>
              ) : (
                renderList(clientDocs)
              )}
              <p className="doc-shelf-foot">
                Signed documents belong here. Anything you share from Internal asks once first.
              </p>
            </section>
            <section className="doc-shelf doc-shelf--internal" aria-label="Internal & audit only">
              <h3 className="doc-card-sec">
                <span className="doc-shelf-title">
                  <Lock size={16} strokeWidth={1.5} aria-hidden />
                  Internal &amp; audit only · {internalDocs.length}
                </span>
                <small>Never shown to the client</small>
              </h3>
              {internalDocs.length === 0 ? (
                <p className="documents-tab-shelf-empty doc-shelf-empty">
                  Nothing here yet. Drop a file, or ask someone to drop one in.
                </p>
              ) : (
                renderList(internalDocs)
              )}
              <p className="doc-shelf-foot">Uploads land here unless you say otherwise.</p>
            </section>
          </>
        )}

        {afterShelves ?? null}

        <div
          className={`doc-drop${dragActive ? " is-drag-active" : ""}`}
          role="button"
          tabIndex={0}
          onClick={() => !uploading && inputRef.current?.click()}
          onKeyDown={(e) => {
            if (uploading) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
        >
          {uploading
            ? "Uploading…"
            : dragActive
              ? "Drop to upload to this record"
              : "Drop files anywhere on this card to upload · stored in SharePoint · 150 MB max"}
        </div>
        {trailing}
      </div>
    );
  }

  return (
    <div className="documents-tab">
      <div
        className={`documents-tab-dropzone${dragActive ? " is-drag-active" : ""}${uploading ? " is-uploading" : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => !uploading && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (uploading) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <div className="documents-tab-dropzone-title">
          {uploading ? "Uploading…" : "Drop files or click to browse"}
        </div>
        <div className="documents-tab-dropzone-sub">Stored in SharePoint · 150 MB max</div>
      </div>
      {/* v1.x phase 11.16 (Tier B item 13): copy from OneDrive. */}
      {entityType === "household" ||
      entityType === "contact" ||
      entityType === "transaction" ||
      entityType === "opportunity" ||
      entityType === "service_record" ||
      entityType === "compliance_checklist_instance" ? (
        <div className="documents-tab-onedrive-row">
          <Button
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              setOneDriveOpen(true);
            }}
            disabled={uploading}
          >
            Copy from OneDrive
          </Button>
          <span className="documents-tab-onedrive-hint">
            Pick a file from your personal OneDrive — it copies into this record's SharePoint
            folder.
          </span>
        </div>
      ) : null}
      {/* v1.x phase 11.16 (Tier B item 11): client-upload magic
          link. Only on entity types with a clear "client" subject —
          household + contact. */}
      {entityType === "household" || entityType === "contact" ? (
        <div className="documents-tab-client-upload-row">
          <Button
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              setLinkModalOpen(true);
            }}
          >
            Client upload links
          </Button>
          <span className="documents-tab-client-upload-hint">
            Generate a magic link the family can open to upload documents — no account required.
          </span>
        </div>
      ) : null}
      {uploadError ? <ErrorBanner message={uploadError} /> : null}
      {listError ? <ErrorBanner message={listError} /> : null}
      {loading ? (
        <p className="documents-tab-loading">Loading documents…</p>
      ) : docs.length === 0 ? (
        <p className="documents-tab-empty">No documents on {entityLabel ?? "this record"} yet.</p>
      ) : (
        // Two shelves (Nate's Aug 24 spec §9, PR5 of 5) — client-visible
        // and internal & audit — rather than one flat list. Both render
        // even when empty: the shelf is a real, named place a document
        // can be, not a filter that only appears once something's in it.
        (["client", "internal"] as const).map((shelf) => {
          // A package's separated pieces are excluded here — they render
          // nested under the combined copy below, not as their own
          // top-level rows. See packageGrouping's doc comment.
          const shelfDocs = docs.filter(
            (d) => shelfOf(d) === shelf && !packageGrouping.childIds.has(d.id),
          );
          return (
            <div key={shelf} className="documents-tab-shelf">
              <h4 className="documents-tab-shelf-title">
                {shelf === "client" ? "Visible to client" : "Internal & audit only"}
              </h4>
              {shelfDocs.length === 0 ? (
                <p className="documents-tab-shelf-empty">
                  {shelf === "client"
                    ? "Nothing shared with the client yet."
                    : "Nothing here — everything's shared with the client."}
                </p>
              ) : (
                <ul className="documents-tab-list">
                  {shelfDocs.map((doc) => {
                    const pieces = packageGrouping.childrenByParent.get(doc.id);
                    if (!pieces) return renderDocumentRow(doc);
                    // Default to ONE document, allow both (Grant,
                    // 2026-09-20) — the combined signed copy renders
                    // normally; its separated pieces sit one click away
                    // rather than as N extra rows for one signature.
                    const expanded = expandedPackages.has(doc.id);
                    return (
                      <Fragment key={doc.id}>
                        {renderDocumentRow(doc)}
                        <li className="documents-tab-package-detail">
                          <button
                            type="button"
                            className="documents-tab-package-toggle"
                            onClick={() => togglePackageExpanded(doc.id)}
                            aria-expanded={expanded}
                            title={
                              expanded
                                ? "Hide the separate documents cut from this package"
                                : "Show each document cut from this package separately"
                            }
                          >
                            {expanded
                              ? "▾ Hide separate documents"
                              : `▸ Show ${pieces.length} separate document${pieces.length === 1 ? "" : "s"}`}
                          </button>
                          {expanded ? (
                            <ul className="documents-tab-list documents-tab-package-children">
                              {pieces.map((piece) => renderDocumentRow(piece))}
                            </ul>
                          ) : null}
                        </li>
                      </Fragment>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })
      )}
      {trailing}
    </div>
  );
}

interface DocumentRowProps {
  doc: DocumentRec;
  onArchive: () => void;
  onRename: () => void;
  // Null when preview isn't available (legacy R2 docs). When set,
  // a "Preview" button appears in the actions column.
  onPreview: (() => void) | null;
  enableStewAnalysis: boolean;
  // Null when e-signature isn't available on this record (no signer
  // candidates, or not a Processing record yet). When set, a "Send for
  // signature" button appears in the actions column.
  onSendForSignature: (() => void) | null;
  // Null when this record has no family to file against — a document on
  // a licence or a compliance checklist has nowhere to move to.
  onMove: (() => void) | null;
  // "Share with client" / "Move to internal" (Nate's Aug 24 spec §9,
  // PR5 of 5) — flips doc.visibility. Always available; every document
  // is on exactly one shelf.
  onToggleVisibility: () => void;
  // Multi-document envelope selection (2026-09-01) — same eligibility
  // as onSendForSignature; when false, no checkbox renders at all rather
  // than a disabled one, since a document that can't be sent can't be
  // put in a package either.
  selectableForEnvelope: boolean;
  selectedForEnvelope: boolean;
  onToggleEnvelopeSelection: () => void;
  /** Record layout: Preview and the shelf flip stay in the open, every
   *  other action moves into the row's menu. */
  compact?: boolean;
  /** The package this signed file came out of, when there is one — it is
   *  what "Signers & history" opens. */
  envelope?: EsignEnvelopeRec | null;
  /** Record layout: the inline "Share with the client?" question. */
  shareConfirmOpen?: boolean;
  onRequestShare?: () => void;
  onCancelShare?: () => void;
  onConfirmShare?: () => void;
}

/** Where a file came from, as the sentence the meta line shows. Only
 *  facts the row carries: an uploader's NAME is not on the document list
 *  today, so "Uploaded" stands on its own rather than guessing. */
export function provenanceOf(doc: DocumentRec, fromPackage: boolean): string {
  const base =
    doc.signed_status === "fully_signed"
      ? fromPackage
        ? "From e-sign · Signed"
        : "Signed"
      : doc.origin === "received"
        ? "They sent this"
        : doc.split_from_document_id
          ? "Cut from a signed package"
          : "Uploaded";
  // Documents tab PR 5: a file that moved here when the Opportunity was
  // won says so, so nobody wonders why it predates the file.
  return doc.carried_from_opportunity_id ? `${base} · carried from the opportunity` : base;
}

/** A small popup menu with outside-click and Esc to close — the same
 *  pattern as the row's download menu and UserMenu.tsx. */
function DocMenu({
  open,
  onOpenChange,
  trigger,
  triggerClassName,
  label,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: ReactNode;
  triggerClassName: string;
  label: string;
  children: ReactNode;
}) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();
  useEffect(() => {
    if (!open) return;
    function onMouseDown(event: MouseEvent) {
      const target = event.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      onOpenChange(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onOpenChange(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);
  return (
    <span className="doc-menu-wrap">
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={label}
        title={label}
        onClick={() => onOpenChange(!open)}
      >
        {trigger}
      </button>
      {open ? (
        <div ref={menuRef} id={menuId} role="menu" className="doc-menu">
          {children}
        </div>
      ) : null}
    </span>
  );
}

function DocumentRow({
  doc,
  onArchive,
  onRename,
  onToggleVisibility,
  selectableForEnvelope,
  selectedForEnvelope,
  onToggleEnvelopeSelection,
  onPreview,
  enableStewAnalysis,
  onSendForSignature,
  onMove,
  compact = false,
  envelope = null,
  shareConfirmOpen = false,
  onRequestShare,
  onCancelShare,
  onConfirmShare,
}: DocumentRowProps) {
  const [rowMenuOpen, setRowMenuOpen] = useState(false);
  const backend = backendOf(doc);
  const toast = useToast();
  // Download href targets the same Worker route for both backends —
  // the Worker either streams (R2) or 302-redirects (SharePoint). The
  // download attribute hints to the browser to save rather than nav-
  // navigate; the redirect destination from SharePoint sets
  // Content-Disposition so this is correct in both cases.
  const downloadHref = `/api/files/download/${encodeURIComponent(doc.id)}`;

  // "Download with certificate" (Nate Swartz, 2026-09-20). Only offered
  // once everyone has signed — a certificate only exists once the
  // envelope has completed, and `signed_status` reaches "fully_signed"
  // exclusively via the e-sign archival rail (shared/db/esign-archive.ts
  // — nothing else in the codebase writes that value), so this is a
  // reliable enough signal to show the option without a per-row query.
  // Whether a certificate actually resolves is checked when clicked,
  // not here.
  const canOfferCertificateDownload = doc.signed_status === "fully_signed";
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const [downloadingWithCertificate, setDownloadingWithCertificate] = useState(false);
  const downloadTriggerRef = useRef<HTMLButtonElement | null>(null);
  const downloadMenuRef = useRef<HTMLDivElement | null>(null);
  const downloadMenuId = useId();

  // Outside-click + Esc to close, same pattern as UserMenu.tsx.
  useEffect(() => {
    if (!downloadMenuOpen) return;
    function onMouseDown(event: MouseEvent) {
      const target = event.target as Node | null;
      if (!target) return;
      if (downloadMenuRef.current?.contains(target)) return;
      if (downloadTriggerRef.current?.contains(target)) return;
      setDownloadMenuOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setDownloadMenuOpen(false);
        downloadTriggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [downloadMenuOpen]);

  const handleDownloadWithCertificate = useCallback(async () => {
    setDownloadMenuOpen(false);
    setDownloadingWithCertificate(true);
    try {
      const cert = await apiV11.getCertificateForSignedDocument(doc.id);
      if (!cert) {
        toast.error("No certificate of completion is filed for this document.");
        return;
      }
      const [signedBlob, certBlob] = await Promise.all([
        apiV11.fetchDocumentBytes(doc.id),
        apiV11.fetchDocumentBytes(cert.certificate_document_id),
      ]);
      const [signedBytes, certBytes] = await Promise.all([
        signedBlob.arrayBuffer(),
        certBlob.arrayBuffer(),
      ]);
      const merged = await mergeSignedDocumentWithCertificate(signedBytes, certBytes);
      const base = doc.filename.replace(/\.pdf$/i, "");
      downloadBlob(`${base} (with certificate).pdf`, merged);
    } catch (err) {
      const message =
        err instanceof CertificateMergeError
          ? err.userMessage
          : err instanceof ApiError
            ? err.message
            : "Couldn't combine the certificate with this document.";
      toast.error(message);
    } finally {
      setDownloadingWithCertificate(false);
    }
  }, [doc.id, doc.filename, toast]);

  // Joey program PR7 — per-row Stew analysis (income + flags). Only when
  // enabled (mortgage context) AND the doc lives in SharePoint (analyzable).
  const [analysis, setAnalysis] = useState<DocumentAnalysis | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const canAnalyze = enableStewAnalysis && Boolean(doc.sharepoint_item_id);

  const runAnalyze = useCallback(async () => {
    setAnalyzing(true);
    setAnalyzeError(null);
    try {
      const res = await apiV11.analyzeDocument(doc.id);
      setAnalysis(res.analysis);
    } catch (err) {
      setAnalyzeError(err instanceof ApiError ? err.message : "Couldn't analyze this document.");
    } finally {
      setAnalyzing(false);
    }
  }, [doc.id]);

  const openAuditTrail = useCallback(async () => {
    setRowMenuOpen(false);
    try {
      const cert = await apiV11.getCertificateForSignedDocument(doc.id);
      if (!cert) {
        toast.error("No certificate of completion is filed for this document.");
        return;
      }
      window.location.assign(
        `/api/files/download/${encodeURIComponent(cert.certificate_document_id)}`,
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't find the audit trail.");
    }
  }, [doc.id, toast]);

  if (compact) {
    const signed = doc.signed_status === "fully_signed";
    const provenance = provenanceOf(doc, Boolean(envelope));
    return (
      <li className={`documents-tab-row doc-row${signed ? " documents-tab-row--signed" : ""}`}>
        {selectableForEnvelope ? (
          <input
            type="checkbox"
            className="documents-tab-row-select"
            checked={selectedForEnvelope}
            onChange={onToggleEnvelopeSelection}
            aria-label={`Include ${doc.filename} in an envelope with other documents`}
            title="Send this together with other checked documents in one envelope"
          />
        ) : null}
        <div className="documents-tab-row-main">
          <a href={downloadHref} className="documents-tab-row-name">
            {doc.filename}
          </a>
          <div className="documents-tab-row-meta">
            <span>{formatUploadedAt(doc.uploaded_at)}</span>
            <span className="documents-tab-row-meta-sep">·</span>
            <span className={signed ? "doc-row-signed" : undefined}>{provenance}</span>
            {envelope?.title ? (
              <>
                <span className="documents-tab-row-meta-sep">·</span>
                <span>{envelope.title}</span>
              </>
            ) : null}
            {doc.signed_status === "partially_signed" ? (
              <>
                <span className="documents-tab-row-meta-sep">·</span>
                <StatusPill tone="info" label="Partly signed" />
              </>
            ) : null}
            {doc.has_issues ? (
              <>
                <span className="documents-tab-row-meta-sep">·</span>
                <span className="documents-tab-row-issue" title={doc.issue_notes ?? "Flagged"}>
                  <AlertTriangle size={14} strokeWidth={1.5} aria-hidden /> Flagged
                </span>
              </>
            ) : null}
            <span className="documents-tab-row-meta-sep">·</span>
            <span>{humanFileSize(doc.file_size_bytes)}</span>
          </div>
        </div>
        <div className="documents-tab-row-actions">
          {onPreview ? (
            <button type="button" onClick={onPreview} className="doc-btn doc-btn--sm">
              Preview
            </button>
          ) : null}
          {doc.visibility === "client" ? (
            <button
              type="button"
              onClick={onToggleVisibility}
              className="doc-btn doc-btn--sm"
              title="Move this document to the internal & audit shelf"
            >
              Make internal
            </button>
          ) : (
            <button
              type="button"
              onClick={onRequestShare ?? onToggleVisibility}
              className="doc-btn doc-btn--sm"
              title="Share this document on the client shelf"
            >
              Share with client
            </button>
          )}
          <DocMenu
            open={rowMenuOpen}
            onOpenChange={setRowMenuOpen}
            trigger={<MoreHorizontal size={16} strokeWidth={1.5} aria-hidden />}
            triggerClassName="doc-btn doc-btn--sm doc-btn--icon"
            label={`More for ${doc.filename}`}
          >
            <a
              href={downloadHref}
              role="menuitem"
              className="doc-menu-item"
              onClick={() => setRowMenuOpen(false)}
            >
              Download
            </a>
            {canOfferCertificateDownload ? (
              <button
                type="button"
                role="menuitem"
                className="doc-menu-item"
                onClick={() => {
                  setRowMenuOpen(false);
                  void handleDownloadWithCertificate();
                }}
              >
                Download with certificate
              </button>
            ) : null}
            {doc.sharepoint_web_url ? (
              <a
                href={resolveMediaUrl(`/api/files/open/${doc.id}`) ?? "#"}
                target="_blank"
                rel="noopener noreferrer"
                role="menuitem"
                className="doc-menu-item"
                onClick={() => setRowMenuOpen(false)}
              >
                Open in SharePoint
              </a>
            ) : null}
            {signed ? (
              <>
                <span className="doc-menu-sep" role="separator" />
                <button
                  type="button"
                  role="menuitem"
                  className="doc-menu-item"
                  onClick={() => void openAuditTrail()}
                >
                  Audit trail
                </button>
                {envelope ? (
                  <Link
                    to={`/esign/${envelope.id}`}
                    role="menuitem"
                    className="doc-menu-item"
                    onClick={() => setRowMenuOpen(false)}
                  >
                    Signers &amp; history
                  </Link>
                ) : null}
              </>
            ) : null}
            <span className="doc-menu-sep" role="separator" />
            <button
              type="button"
              role="menuitem"
              className="doc-menu-item"
              onClick={() => {
                setRowMenuOpen(false);
                onRename();
              }}
            >
              Rename
            </button>
            {onMove ? (
              <button
                type="button"
                role="menuitem"
                className="doc-menu-item"
                onClick={() => {
                  setRowMenuOpen(false);
                  onMove();
                }}
              >
                File under…
              </button>
            ) : null}
            {onSendForSignature ? (
              <button
                type="button"
                role="menuitem"
                className="doc-menu-item"
                onClick={() => {
                  setRowMenuOpen(false);
                  onSendForSignature();
                }}
              >
                Prepare &amp; send
              </button>
            ) : null}
            {canAnalyze ? (
              <button
                type="button"
                role="menuitem"
                className="doc-menu-item"
                disabled={analyzing}
                onClick={() => {
                  setRowMenuOpen(false);
                  void runAnalyze();
                }}
              >
                {analyzing ? "Analyzing…" : "Analyze with Stew"}
              </button>
            ) : null}
            <span className="doc-menu-sep" role="separator" />
            <button
              type="button"
              role="menuitem"
              className="doc-menu-item doc-menu-item--danger"
              onClick={() => {
                setRowMenuOpen(false);
                onArchive();
              }}
            >
              Archive
            </button>
          </DocMenu>
        </div>
        {shareConfirmOpen ? (
          <div className="doc-share-confirm" role="alertdialog" aria-label="Share with the client?">
            <span>
              <strong>Share &quot;{doc.filename}&quot; with the client?</strong> It moves to Visible
              to client and they can open it.
            </span>
            <span className="doc-share-confirm-actions">
              <button
                type="button"
                className="doc-btn doc-btn--sm doc-btn--primary"
                onClick={onConfirmShare}
              >
                Share it
              </button>
              <button type="button" className="doc-btn doc-btn--sm" onClick={onCancelShare}>
                Keep internal
              </button>
            </span>
          </div>
        ) : null}
        {analyzeError ? (
          <div className="documents-tab-row-analysis-error">{analyzeError}</div>
        ) : null}
        {analysis ? (
          <DocumentAnalysisPanel analysis={analysis} onClose={() => setAnalysis(null)} />
        ) : null}
      </li>
    );
  }

  return (
    <li
      className={`documents-tab-row${doc.signed_status === "fully_signed" ? " documents-tab-row--signed" : ""}`}
    >
      {selectableForEnvelope ? (
        <input
          type="checkbox"
          className="documents-tab-row-select"
          checked={selectedForEnvelope}
          onChange={onToggleEnvelopeSelection}
          aria-label={`Include ${doc.filename} in an envelope with other documents`}
          title="Send this together with other checked documents in one envelope"
        />
      ) : null}
      <div className="documents-tab-row-main">
        <div className="documents-tab-row-name-line">
          <a
            href={downloadHref}
            className="documents-tab-row-name"
            // Don't open in a new tab for downloads — same-tab is the
            // expected behavior on file-list clicks.
          >
            {doc.filename}
          </a>
          <button
            type="button"
            className="documents-tab-row-rename"
            onClick={onRename}
            aria-label={`Rename ${doc.filename}`}
            title="Rename"
          >
            <Pencil size={14} strokeWidth={1.5} aria-hidden />
          </button>
        </div>
        <div className="documents-tab-row-meta">
          <span className="documents-tab-row-size">{humanFileSize(doc.file_size_bytes)}</span>
          <span className="documents-tab-row-meta-sep">·</span>
          <span className="documents-tab-row-uploaded">{formatUploadedAt(doc.uploaded_at)}</span>
          {backend === "sharepoint" ? (
            <>
              <span className="documents-tab-row-meta-sep">·</span>
              <span
                className="documents-tab-row-backend documents-tab-row-backend-sharepoint"
                title="Stored in SharePoint"
              >
                SharePoint
              </span>
            </>
          ) : null}
          {/* Where this document has got to in signing, via the real
              StatusPill primitive rather than hand-rolled colored text
              (Joey Ellis, 2026-09-08: the plain gray label this used to
              be — see git history — was easy to miss entirely after a
              document came back signed; a Charles Watson envelope he
              sent filed correctly but he never spotted it here). Green
              dot = done; the row itself also picks up a success tint
              below. "Partly signed" is the one that had never been
              visible at all before 2026-08-24 — it means "chase the
              other person", not "nothing happened", so it stays its own
              distinct (blue) state rather than folding into "Signed". */}
          {doc.signed_status === "partially_signed" || doc.signed_status === "fully_signed" ? (
            <>
              <span className="documents-tab-row-meta-sep">·</span>
              <StatusPill
                tone={doc.signed_status === "fully_signed" ? "active" : "info"}
                label={doc.signed_status === "fully_signed" ? "Signed" : "Partly signed"}
                title={
                  doc.signed_status === "fully_signed"
                    ? "Everyone has signed"
                    : "Someone has signed — others haven't yet"
                }
              />
              {doc.signed_status === "fully_signed" && doc.signed_at ? (
                <>
                  <span className="documents-tab-row-meta-sep">·</span>
                  <span>Signed {formatUploadedAt(doc.signed_at)}</span>
                </>
              ) : null}
            </>
          ) : null}
          {doc.has_issues ? (
            <>
              <span className="documents-tab-row-meta-sep">·</span>
              <span
                className="documents-tab-row-issue"
                title={doc.issue_notes ?? "Flagged with an issue"}
              >
                <AlertTriangle size={14} strokeWidth={1.5} aria-hidden /> Flagged
              </span>
            </>
          ) : null}
          {doc.verified_at ? (
            <>
              <span className="documents-tab-row-meta-sep">·</span>
              <span className="documents-tab-row-verified" title="Verified">
                <Check size={14} strokeWidth={1.5} aria-hidden /> Verified
              </span>
            </>
          ) : null}
        </div>
      </div>
      <div className="documents-tab-row-actions">
        {canOfferCertificateDownload ? (
          <div className="documents-tab-row-download">
            <button
              ref={downloadTriggerRef}
              type="button"
              onClick={() => setDownloadMenuOpen((open) => !open)}
              className="documents-tab-row-action documents-tab-row-action-download"
              disabled={downloadingWithCertificate}
              aria-haspopup="menu"
              aria-expanded={downloadMenuOpen}
              aria-controls={downloadMenuId}
              title="Download this signed document"
            >
              {downloadingWithCertificate ? "Combining…" : "Download"}
              <ChevronDown size={13} strokeWidth={2} aria-hidden />
            </button>
            {downloadMenuOpen ? (
              <div
                ref={downloadMenuRef}
                id={downloadMenuId}
                role="menu"
                className="documents-tab-row-download-menu"
              >
                {/* Nate Swartz, 2026-09-20: "I nearly always send the
                    non certified document" — plain download stays the
                    default, first option, at the sealed PDF's own URL. */}
                <a
                  href={downloadHref}
                  role="menuitem"
                  className="documents-tab-row-download-menu-item"
                  onClick={() => setDownloadMenuOpen(false)}
                >
                  Download
                </a>
                <button
                  type="button"
                  role="menuitem"
                  className="documents-tab-row-download-menu-item"
                  onClick={() => void handleDownloadWithCertificate()}
                >
                  Download with certificate
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
        {onPreview ? (
          <button
            type="button"
            onClick={onPreview}
            className="documents-tab-row-action documents-tab-row-action-preview"
            title="Preview inline"
          >
            Preview
          </button>
        ) : null}
        {onSendForSignature ? (
          <button
            type="button"
            onClick={onSendForSignature}
            className="documents-tab-row-action documents-tab-row-action-esign"
            // ONE NAME FOR ONE ACTION (2026-09-15). This row and the bar
            // above it open the same screen; they said "Send for
            // signature" and "Prepare & send", which is how ten menu
            // paths ended up under six names (walkthrough break 5/21).
            title="Prepare and send this document for signature"
          >
            Prepare &amp; send
          </button>
        ) : null}
        {onMove ? (
          <button
            type="button"
            onClick={onMove}
            className="documents-tab-row-action"
            title="File this document under a different record"
          >
            File under…
          </button>
        ) : null}
        {doc.sharepoint_web_url ? (
          // Advisor-only deep-link to SharePoint's web UI. NEVER
          // surfaced on client-facing routes — the source-of-truth
          // principle keeps clients on app-brokered URLs only.
          //
          // The href is the OS's own open endpoint, not the stored
          // sharepoint_web_url: the OS grants access to this one file
          // and then redirects. `sharepoint_web_url` is still what
          // tells us the row HAS something to open.
          <a
            href={resolveMediaUrl(`/api/files/open/${doc.id}`) ?? "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="documents-tab-row-action documents-tab-row-action-open"
            title="Open in SharePoint (advisor-only)"
          >
            Open in SharePoint
          </a>
        ) : null}
        {canAnalyze ? (
          <button
            type="button"
            onClick={() => void runAnalyze()}
            disabled={analyzing}
            className="documents-tab-row-action documents-tab-row-action-analyze"
            title="Stew extracts income + flags from this document"
          >
            <Sparkles size={13} strokeWidth={1.5} aria-hidden />{" "}
            {analyzing ? "Analyzing…" : "Analyze with Stew"}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onToggleVisibility}
          className="documents-tab-row-action documents-tab-row-action-visibility"
          title={
            doc.visibility === "client"
              ? "Move this document to the internal & audit shelf"
              : "Share this document on the client shelf"
          }
        >
          {doc.visibility === "client" ? "Move to internal" : "Share with client"}
        </button>
        {/* A plain button, not the ghost <Button>: every other control in
            this row is a .documents-tab-row-action, and the ghost variant
            brought its own size and padding so Archive sat at a different
            height from its five neighbours. Same class, danger ink. */}
        <button
          type="button"
          onClick={onArchive}
          className="documents-tab-row-action documents-tab-row-action-archive"
          title="Archive this document"
        >
          Archive
        </button>
      </div>
      {analyzeError ? (
        <div
          className="documents-tab-row-analysis-error"
          style={{ fontSize: 12, color: "var(--danger)", padding: "6px 0 0" }}
        >
          {analyzeError}
        </div>
      ) : null}
      {analysis ? (
        <DocumentAnalysisPanel analysis={analysis} onClose={() => setAnalysis(null)} />
      ) : null}
    </li>
  );
}

// Joey program PR7 — renders Stew's document analysis (income + flags).
// AI-generated, labeled as such, never masquerading as a human note.
function DocumentAnalysisPanel({
  analysis,
  onClose,
}: {
  analysis: DocumentAnalysis;
  onClose: () => void;
}) {
  const flagColor = (sev: "green" | "red" | "info"): string =>
    sev === "red" ? "var(--danger)" : sev === "green" ? "var(--success)" : "var(--fg-muted)";
  return (
    <div
      style={{
        marginTop: 10,
        padding: "12px 14px",
        border: "1px solid var(--mid-gray)",
        borderLeft: "3px solid var(--attention)",
        borderRadius: 8,
        background: "var(--bg-1)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontFamily: "Gotham, Montserrat, sans-serif",
            fontWeight: 800,
            fontSize: 10,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "var(--text-blue)",
          }}
        >
          <Sparkles size={12} aria-hidden /> Stew · {analysis.document_kind}
        </span>
        <button
          type="button"
          onClick={onClose}
          style={{
            marginLeft: "auto",
            border: "none",
            background: "none",
            cursor: "pointer",
            color: "var(--fg-muted)",
            fontSize: 12,
          }}
        >
          Dismiss
        </button>
      </div>

      {analysis.qualifying_income_note ? (
        <p style={{ fontSize: 13, color: "var(--fg-1)", margin: "0 0 10px", lineHeight: 1.5 }}>
          {analysis.qualifying_income_note}
        </p>
      ) : null}

      {analysis.income_items.length > 0 ? (
        <div style={{ marginBottom: 10 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: "var(--fg-muted)",
              marginBottom: 4,
            }}
          >
            Income found
          </div>
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12.5, color: "var(--fg-2)" }}>
            {analysis.income_items.map((it, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                <strong>{it.amount}</strong>
                {it.period ? ` ${it.period}` : ""} — {it.source}
                {it.note ? <span style={{ color: "var(--fg-muted)" }}> · {it.note}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {analysis.flags.length > 0 ? (
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: "var(--fg-muted)",
              marginBottom: 4,
            }}
          >
            Flags
          </div>
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            {analysis.flags.map((f, i) => (
              <li key={i} style={{ fontSize: 12.5, color: "var(--fg-2)" }}>
                <span style={{ color: flagColor(f.severity), fontWeight: 700 }}>
                  {f.severity === "red" ? "● " : f.severity === "green" ? "● " : "○ "}
                  {f.label}
                </span>
                {f.detail ? <span style={{ color: "var(--fg-muted)" }}> — {f.detail}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div style={{ fontSize: 10.5, color: "var(--fg-muted)", marginTop: 10, lineHeight: 1.4 }}>
        AI-generated decision support · review against the source document. Not a final underwriting
        determination.
      </div>
    </div>
  );
}
